import { useState } from "react";
import { Link } from "react-router-dom";
import { Save, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Field, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import DocumentsPanel from "../components/DocumentsPanel";
import EquipmentPanel from "../components/EquipmentPanel";
import { ROLE_LABEL, fmtDate } from "../lib/format";

function MyEquipment({ workerId }) {
  const { data, reload } = useApi(`/workers/${workerId}`);
  return (
    <section className="surface p-5 lg:col-span-2" data-testid="my-equipment"><h2 className="font-semibold mb-3">Attrezzi e dotazioni affidate</h2>
      {data && <EquipmentPanel workerId={workerId} items={data.equipment} canEdit={false} reload={reload} />}</section>
  );
}

function WorkerExtras({ workerId }) {
  const { data } = useApi("/dashboard");
  if (!data) return null;
  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-6">
      <section className="surface p-5"><h2 className="font-semibold mb-3">Prossimi turni</h2>
        {!data.upcoming.length ? <Empty title="Nessun turno" /> : data.upcoming.map((a) => <div key={a.id} className="flex justify-between py-2 border-b border-border/60 text-sm"><span>{a.site_name}</span><span className="num">{fmtDate(a.start_date)} → {fmtDate(a.end_date)}</span></div>)}</section>
      <section className="surface p-5"><h2 className="font-semibold mb-3">Storico presenze</h2>
        {!data.history.length ? <Empty title="Nessuno storico" /> : data.history.map((a) => <div key={a.id} className="flex justify-between py-2 border-b border-border/60 text-sm"><span>{a.site_name}</span><span className="num">{fmtDate(a.start_date)} → {fmtDate(a.end_date)}</span></div>)}</section>
      <section className="surface p-5"><h2 className="font-semibold mb-3">I miei documenti</h2><DocumentsPanel entityType="worker" entityId={workerId} canEdit={false} /></section>
      <section className="surface p-5"><h2 className="font-semibold mb-2">Notifiche</h2><p className="text-sm text-muted-foreground mb-3">{data.unread_notifications} notifiche da leggere.</p><Link to="/notifiche" className="text-amber-400 text-sm">Apri centro notifiche →</Link></section>
      <MyEquipment workerId={workerId} />
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ first_name: user.first_name, last_name: user.last_name, email: user.email, phone: user.phone || "" });
  const [p, setP] = useState({ current_password: "", new_password: "", confirm: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      const r = await api.put("/auth/me", f);
      setUser({ ...user, ...r.data });
      toast.success("✓ Profilo aggiornato");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const changePwd = async (e) => {
    e.preventDefault();
    if (p.new_password !== p.confirm) return toast.error("Le password non coincidono");
    try {
      await api.post("/auth/change-password", p);
      toast.success("✓ Password modificata");
      setP({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Account" title="Il mio profilo" subtitle={ROLE_LABEL[user.role]} />
      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={save} className="surface p-5 grid sm:grid-cols-2 gap-3" data-testid="profile-form">
          <Field label="Nome"><Input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="profile-first-name" /></Field>
          <Field label="Cognome"><Input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="profile-last-name" /></Field>
          <Field label="Email"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="profile-email" /></Field>
          <Field label="Telefono"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="profile-phone" /></Field>
          <Field label="Ruolo"><Input value={ROLE_LABEL[user.role]} disabled /></Field>
          <div className="sm:col-span-2"><Button type="submit" data-testid="profile-save-btn"><Save className="h-4 w-4 mr-2" />Salva profilo</Button></div>
        </form>
        <form onSubmit={changePwd} className="surface p-5 space-y-3" data-testid="password-form">
          <h2 className="font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4 text-amber-400" />Modifica password</h2>
          <Field label="Password attuale"><Input type="password" required value={p.current_password} onChange={(e) => setP({ ...p, current_password: e.target.value })} data-testid="pwd-current" /></Field>
          <Field label="Nuova password (min 8 caratteri)"><Input type="password" required value={p.new_password} onChange={(e) => setP({ ...p, new_password: e.target.value })} data-testid="pwd-new" /></Field>
          <Field label="Conferma nuova password"><Input type="password" required value={p.confirm} onChange={(e) => setP({ ...p, confirm: e.target.value })} data-testid="pwd-confirm" /></Field>
          <Button type="submit" data-testid="pwd-save-btn">Aggiorna password</Button>
        </form>
      </div>
      {user.role === "operaio" && user.worker_id && <WorkerExtras workerId={user.worker_id} />}
    </div>
  );
}
